// The relay's mirror adopting another life for an agent (knowledge/125): a reset frame, or
// a page whose cursor named another life. The client adopts it through adoptNewLife.

export interface MirrorRevision {
  authorityEpoch: number;
  lifeGen: number;
}

export interface MirrorReset {
  agentId: string;
  life?: string;
  revision?: MirrorRevision;
}

export type MirrorResetOutcome = "older" | "same" | "adopt";

function isOlder(held: MirrorRevision, incoming: MirrorRevision): boolean {
  if (incoming.authorityEpoch !== held.authorityEpoch) {
    return incoming.authorityEpoch < held.authorityEpoch;
  }
  return incoming.lifeGen < held.lifeGen;
}

// The newest revision a reset carried, per agent: a reset older than it is a late frame.
export class MirrorRevisions {
  private readonly held = new Map<string, MirrorRevision>();

  // False for a revision older than the one held; records every other one.
  admit(agentId: string, revision: MirrorRevision | undefined): boolean {
    if (!revision) return true;
    const held = this.held.get(agentId);
    if (held && isOlder(held, revision)) return false;
    this.held.set(agentId, revision);
    return true;
  }

  reset(): void {
    this.held.clear();
  }

  size(): number {
    return this.held.size;
  }
}

// What a reset asks of the view: nothing when it is older than one already seen, or names
// no life or the life on screen; otherwise an adoption of its life.
export function mirrorResetOutcome(
  reset: MirrorReset,
  revisions: MirrorRevisions,
  lifeReplaces: (agentId: string, life: string) => boolean,
): MirrorResetOutcome {
  if (!revisions.admit(reset.agentId, reset.revision)) return "older";
  if (!reset.life || !lifeReplaces(reset.agentId, reset.life)) return "same";
  return "adopt";
}

// A page that is not the whole history: an unverified mirror life, or one whose
// completeness below a seq is not guaranteed (knowledge/125).
export function pageIncomplete(page: {
  verified?: boolean;
  coverage?: { unguaranteedBelow: number };
}): boolean {
  return page.verified === false || (page.coverage?.unguaranteedBelow ?? 0) > 0;
}

// A page that proves nothing older exists when it says so: a daemon page (not mirror or blind)
// that STATED `hasOlder` (an older daemon's omitted field reads false) and is whole.
export function olderProof(page: {
  hasOlderStated?: boolean;
  source?: "mirror" | "blind";
  verified?: boolean;
  coverage?: { unguaranteedBelow: number };
}): boolean {
  return page.hasOlderStated === true && page.source === undefined && !pageIncomplete(page);
}

// A new life with no rows the mirror holds (knowledge/125): an empty page the mirror vouches
// for, or one that declares what it misses. Not a read that missed.
export function lifeRetired(page: {
  items: unknown[];
  verified?: boolean;
  coverage?: { unguaranteedBelow: number };
}): boolean {
  if (page.items.length > 0) return false;
  return page.verified === true || (page.coverage?.unguaranteedBelow ?? 0) > 0;
}

// Where an older read leaves the history: a reset page (its cursor named another life,
// knowledge/125) is empty on purpose, so its cursor is spent but the history goes on (#3629).
export function olderPagination(page: {
  hasOlder: boolean;
  olderCursor: string | null;
  reset?: boolean;
}): { hasOlder: boolean; olderCursor: string | null } {
  if (page.reset === true) return { hasOlder: true, olderCursor: null }; // null: spent
  return { hasOlder: page.hasOlder, olderCursor: page.olderCursor };
}

interface LifeView {
  lifeDiffers(agentId: string, life: string | undefined): boolean;
  hydrateFromTimeline(agentId: string, items: [], meta: Record<string, unknown>): boolean;
  streams: { get(agentId: string): { life?: string; liveLife?: string } | undefined };
}

// The adoption read proved no life: empty the view, marked incomplete, and forget its life.
export function dropViewOfOldLife(
  view: LifeView,
  agentId: string,
  life: string | undefined,
): boolean {
  if (life === undefined || !view.lifeDiffers(agentId, life)) return false;
  if (!view.hydrateFromTimeline(agentId, [], { mode: "rewind", incomplete: true, retired: true })) {
    return false;
  }
  const stream = view.streams.get(agentId);
  if (stream) {
    stream.life = undefined;
    stream.liveLife = undefined;
  }
  return true;
}

export function notePageProvenance(
  s: {
    hydratedFromCache: boolean;
    hydratedFromMirror: boolean;
    lifeUnproven: boolean;
    historyIncomplete: boolean;
    life?: string;
    entries: readonly { seq?: number }[];
    copyTailSeq?: number;
  },
  meta: { fromCache?: boolean; source?: string; incomplete?: boolean; life?: string } | undefined,
): void {
  s.hydratedFromCache = meta?.fromCache === true;
  s.hydratedFromMirror = meta?.source === "mirror";
  const copy = s.hydratedFromCache || s.hydratedFromMirror;
  s.copyTailSeq = copy ? s.entries.reduce((top, e) => Math.max(top, e.seq ?? 0), 0) : undefined;
  s.lifeUnproven = meta?.source === "mirror" && meta.life === undefined && s.life === undefined;
  s.historyIncomplete = meta?.incomplete === true;
}

// A server page was accepted, so the stream exists after all (AgentStream.timelineGone).
export function rearmTimelineGone(s: { timelineGone: boolean }, fromCache: boolean): void {
  if (!fromCache) s.timelineGone = false;
}
